import { useMemo } from "react";

import type { FlexStationSummary } from "./summarizeFlexStations";

export type PropertyMapEntry = Readonly<{
  uid: string;
  /** Address shown when the flexstation summary has no street address (e.g. station name). */
  fallbackAddress?: string | null;
  summary?: FlexStationSummary;
  secondaryId?: string | null;
  targetTemp?: number | null;
  /** Last-1-hour indoor temperature ("Current average" column). */
  currentTemp?: number | null;
  avgTemp24h?: number | null;
  energyMwh?: number | null;
}>;

export type PropertyUidMaps = {
  addressByUid: Map<string, string | null>;
  flexStationIdByUid: Map<string, string | null>;
  numberOfSensorsByUid: Map<string, number>;
  secondaryIdByUid: Map<string, string | null>;
  targetTempByUid: Map<string, number | null>;
  currentTempByUid: Map<string, number | null>;
  avgTemp24hByUid: Map<string, number | null>;
  energyMwhByUid: Map<string, number>;
};

/**
 * Build the by-UID lookup maps the All Properties table/KPI components consume,
 * from a normalized per-property entry list. Shared by the Platform FlexStation
 * overview and the PropAI org Dashboard so the Map assembly lives in one place.
 *
 * Callers should memoize `entries` (its identity drives the memo).
 */
export function usePropertyUidMaps(entries: PropertyMapEntry[]): PropertyUidMaps {
  return useMemo(() => {
    const maps: PropertyUidMaps = {
      addressByUid: new Map(),
      flexStationIdByUid: new Map(),
      numberOfSensorsByUid: new Map(),
      secondaryIdByUid: new Map(),
      targetTempByUid: new Map(),
      currentTempByUid: new Map(),
      avgTemp24hByUid: new Map(),
      energyMwhByUid: new Map(),
    };

    for (const entry of entries) {
      if (!entry?.uid) continue;
      const { uid, summary } = entry;
      maps.addressByUid.set(uid, summary?.streetAddress ?? entry.fallbackAddress ?? null);
      maps.flexStationIdByUid.set(uid, summary?.flexStationId ?? null);
      maps.numberOfSensorsByUid.set(uid, summary?.numberOfSensors ?? 0);
      maps.secondaryIdByUid.set(uid, entry.secondaryId ?? null);
      maps.targetTempByUid.set(uid, entry.targetTemp ?? null);
      maps.currentTempByUid.set(uid, entry.currentTemp ?? null);
      maps.avgTemp24hByUid.set(uid, entry.avgTemp24h ?? null);
      maps.energyMwhByUid.set(uid, entry.energyMwh ?? 0);
    }

    return maps;
  }, [entries]);
}
